import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import { IItemsRequestsApp, IRequestedItem, ItemsRequestModel } from ".";
import { GridDataColumns, IGridDataConfig } from "../../components/GridData";
import BaseApp, { IBaseAppDynamicBasePage, IBaseAppFormConfig, IGridDataAdditional, ISearchParam } from "../BaseApp";
import { useReturnItemsPagination } from "../items/itemsSlice";
import { addNewItemsRequest, deleteItemsRequest, setSelectedItemsRequests, updateAllItemsRequests, updateItemsRequest, updateItemsRequestsLoadingStatus, updateItemsRequestsPagination, updateItemsRequestsSearch, updateItemsRequestsSearchParams, useReturnAllItemRequests, useReturnItemsRequestsLoadingStatus, useReturnItemsRequestsPagination, useReturnItemsRequestsSearch, useReturnItemsRequestsSearchParams, useReturnSelectedItemsRequests } from "./itemsRequestsSlice";
import { ISelectFieldSingleItem } from "../../components/Form/DynamicForm";
import { ICellRendererParams } from "ag-grid-community";
import { Link } from "react-router-dom";
import api from "../../services/api";
import UsersApp from "../user/UsersApp";
import ProjectsApp from "../projects/ProjectsApp";
import { IAttachment } from "../../components/Attachments";
import { JSX } from "react/jsx-runtime";
import SingleRequest from "./SingleRequest";

/**
 * Empty Request
 */
export const EmptyItemRequestModel: ItemsRequestModel = {
    id: 0,
    status: "pending",
    category: "office",
    designation_type: "warehouse",
    action_type: "use",
    note: "",
    project_id: null,
    requested_by: 0,
    reviewed_by: null,
    created_at: "",
    updated_at: null,
    reviewed_at: null,
    review_note: "",
    request_number: "",
    title: "",
    destination_warehouse: null,
    destination_section: null,
    destination_location: null,
    destination_user: null,
    project_title: "",
    request_by_name: "",
    reviewed_by_name: "",
    destination_user_name: "",
    received_at: null,
    returning_at: null,
    transit_at: null,
    returned_at: null,
    closed_at: null,
    start_date: null,
    return_by_date: null,
    is_overdue: false,
}

export const itemsReuestStatusTypes: ISelectFieldSingleItem[] = [
    {
        name: "Pending",
        value: "pending"
    },
    {
        name: "Approved",
        value: "approved"
    },
    {
        name: "Preparing",
        value: "preparing"
    },
    {
        name: "Ready",
        value: "ready"
    },
    {
        name: "In Transit",
        value: "transit"
    },
    {
        name: "Fulfilled",
        value: "fulfilled"
    },
    {
        name: "Returning",
        value: "returning"
    },
    {
        name: "Returned",
        value: "returned"
    },
    {
        name: "Closed",
        value: "closed"
    },
    {
        name: "Rejected",
        value: "rejected"
    },
]

export const itemsReuestDesignationTypes: ISelectFieldSingleItem[] = [
    {
        name: "Warehouse",
        value: "warehouse"
    },
    {
        name: "Section",
        value: "section"
    },
    {
        name: "Location",
        value: "location"
    },
]

export const itemsReuestActionTypes: ISelectFieldSingleItem[] = [
    {
        name: "Use",
        value: "use"
    },
    {
        name: "Move",
        value: "move"
    },
    // {
    //     name: "Transfer",
    //     value: "transfer"
    // },
]

export const itemsRequestCategories: ISelectFieldSingleItem[] = [
    {
        name: "Use for Project",
        value: "project"
    },
    {
        name: "Relocate Permanently",
        value: "relocate"
    },
    {
        name: "Use in Office",
        value: "office"
    },
    {
        name: "Temporary Personal Use",
        value: "personal"
    },
    {
        name: "Testing Purpose",
        value: "testing"
    },
]

/**
 * Items Requests App
 */
export default class ItemsRequestsApp extends BaseApp implements IItemsRequestsApp {

    appName = "item_requests"
    appBaseEndPoint = "/items/requests"

    customListEndPoint: string = this.appBaseEndPoint

    elemPrefix: string = "item_requests_"

    isBeta: boolean = true

    messages = { 
        create: "Create a request",
        createShort: "Create",
        edit: "Edit request",
        editShort: "Edit",
        delete: "Delete request", 
        deleteShort: "Delete",
        view: "View request",
        nothingFound: "No requested have been found.",
    };

    /**
     * Dynamic Base Page
     */
    dynamicBasePage: IBaseAppDynamicBasePage = {
        items: useReturnAllItemRequests,
        pagination: useReturnItemsRequestsPagination,
        loading: useReturnItemsRequestsLoadingStatus,
        search: useReturnItemsRequestsSearch,
        updateLoadingStatus: (status: boolean, dispatch: Dispatch<UnknownAction>) => dispatch(updateItemsRequestsLoadingStatus({ loading: status })),
        deleteItem: (id: number, dispatch: Dispatch<UnknownAction>) => dispatch(deleteItemsRequest({ id: id })),
        addNewItem: (item, dispatch) => dispatch(addNewItemsRequest((item as ItemsRequestModel))),
        updateItem: (item, dispatch) => dispatch(updateItemsRequest((item as ItemsRequestModel))),
        updateAllItems: (items, dispatch) => dispatch(updateAllItemsRequests((items as ItemsRequestModel[]))),
        updateSearch: (search: string, dispatch: Dispatch<UnknownAction>) => dispatch(updateItemsRequestsSearch({ search: search })),
        updateItemsPagination: (pagination, dispatch) => {
            dispatch(updateItemsRequestsPagination(pagination))
        },
        itemsActionOptions: [
            {
                icon: "edit",
                label: "Edit",
                link: "/panel/item_requests/edit/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/edit/" + rawItem.id)
                },
            },
            {
                icon: "show",
                label: "View",
                link: "/panel/item_requests/view/[id]",
                onClick: (rawItem) => {
                    // navigate("/panel/items/view/" + rawItem.id)
                },
            },
        ],
    }

    quickPreview = (id: number) => {
        return <SingleRequest requestNumber={String(id)} />
    }

    /**
     * Grid data
     */
    gridDataColumns: GridDataColumns = [
        {
            field: "request_number",
            headerName: "Request No.",
            width: 250,
            type: "text",
            cellRenderer: (params: ICellRendererParams<ItemsRequestModel>) => {
                const item = params.data

                if (!item){
                    return params.value
                }

                return(
                    <div className="grid-data-current-item-location-box">
                        <Link className="data-grid-link" to={`/panel/item_requests/view/${item.request_number}`}>
                            <h3 className="title">{item.request_number}</h3>
                            {item.request_number !== item.title && <p className="subtitle">{item.title}</p>}
                        </Link>
                    </div>
                )
            }
        },
        {
            field: "status",
            headerName: "Status",
            width: 150,
            type: "status",
        },
        {
            field: "action_type",
            headerName: "Request Type",
            width: 200,
            type: "status",
        },
        {
            field: "category",
            headerName: "Category",
            width: 200,
            type: "status",
        },
        {
            field: "project_title",
            headerName: "Project title",
            width: 200,
            type: "quick-preview",
            cellRendererParams: {
                appClass: () => new ProjectsApp(),
                elemIdTitle: "project_id"
            }
        },
        {
            field: "request_by_name",
            headerName: "Requested by",
            width: 250,
            type: "quick-preview",
            cellRendererParams: {
                appClass: () => new UsersApp(),
                elemIdTitle: "requested_by"
            }
        },
        {
            field: "reviewed_by_name",
            headerName: "Reviewed by",
            width: 250,
            type: "quick-preview",
            cellRendererParams: {
                appClass: () => new UsersApp(),
                elemIdTitle: "reviewed_by"
            }
        },
        {
            field: "reviewed_at",
            headerName: "Reviewed on",
            type: "date",
            width: 250,
        },
        {
            field: "created_at",
            headerName: "Date Created",
            type: "date",
            width: 250,
        },
        {
            field: "start_date",
            headerName: "Start on",
            type: "date",
            width: 250,
        },
        {
            field: "return_by_date",
            headerName: "Return by",
            type: "date",
            width: 250,
        },
        {
            field: "updated_at",
            headerName: "Last Updated",
            type: "date",
            width: 250,
        }
    ]
    useGridData: boolean = true
    gridConfig: IGridDataConfig = {
        viewItemLink: "/panel/item_requests/view/[request_number]",
        editItemLink: "/panel/item_requests/edit/[request_number]",
        disableDelete: true,
        disableActionButtons: true,
        disableSelect: true,
        filterForm: {
            name: "requests-list-filter-form",
            elements: [
                {
                    id: "status",
                    type: "Select",
                    xs: 4,
                    label: "Status",
                    fullWidth: true,
                    items: itemsReuestStatusTypes,
                    addNoneItem: true,
                },
                {
                    id: "action_type",
                    type: "Select",
                    xs: 4,
                    label: "Type",
                    fullWidth: true,
                    items: itemsReuestActionTypes,
                    addNoneItem: true,
                },
                {
                    id: "category",
                    type: "Select",
                    xs: 4,
                    label: "Category",
                    fullWidth: true,
                    items: itemsRequestCategories,
                    addNoneItem: true,
                },
                {
                    id: "requested_by",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Requested by",
                    fullWidth: true,
                    appClass: new UsersApp(),
                    selectLabel: "Select User",
                    gridColumns: new UsersApp().gridDataColumns,
                    disableAddNewItem: true,
                },
                {
                    id: "reviewed_by",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Reviewed by",
                    fullWidth: true,
                    appClass: new UsersApp(),
                    selectLabel: "Select User",
                    gridColumns: new UsersApp().gridDataColumns,
                    disableAddNewItem: true,
                },
                {
                    id: "project_id",
                    xs: 4,
                    type: "DynamicModelSelect",
                    label: "Project",
                    fullWidth: true,
                    appClass: new ProjectsApp(),
                    selectLabel: "Select Project",
                    gridColumns: new ProjectsApp().gridDataColumns,
                    disableAddNewItem: true,
                }
            ]
        }
    }
    gridDataEssentials: IGridDataAdditional  = {
        selectedItems: useReturnSelectedItemsRequests,
        searchParams: useReturnItemsRequestsSearchParams,
        actionButtons: [],
        headerOptions: [],
        setSelectedItems: (items, dispatch) => dispatch(setSelectedItemsRequests(items)),
        updateItemSearchParams: (params, dispatch) => dispatch(updateItemsRequestsSearchParams(params))
    }

    /**
     * List of instances
     */
    myRequestsList = async(page: number, search: string | null, disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => {

        let path: string = this.appBaseEndPoint + "/"

        if (this.customListEndPoint){
            path = this.customListEndPoint
        }

        path += `?page=${page ? page : 1}&my_requests=1`

        if (search !== null){
            path += "&search=" + search
        }

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        return await api.get(path)

    }

    // formConfig: IBaseAppFormConfig = {
    //     form: {
    //         name: "items_request_form",
    //         elements: [
    //             {
    //                 id: this.elemPrefix + "item_id",
    //                 xs: 6,
    //                 // type: "DynamicSelect",
    //                 type: "SelectItem",
    //                 label: "Item",
    //                 fullWidth: true,
    //                 itemsList: useReturnAllItems,
    //                 modelTitleParam: "title",
    //                 appClass: new ItemsApp(),
    //                 addNewItem: addNewItem,
    //             },
    //             {
    //                 id: this.elemPrefix + "issue_type",
    //                 xs: 6,
    //                 type: "Select",
    //                 label: "Type",
    //                 fullWidth: true,
    //                 items: issuesTypesList
    //             },
    //             {
    //                 id: this.elemPrefix + "description",
    //                 xs: 12,
    //                 type: "BlockEditor",
    //                 label: "Description",
    //                 fullWidth: true,
    //             },
    //             {
    //                 id: this.elemPrefix + "reported_by",
    //                 xs: 6,
    //                 type: "DynamicSelect",
    //                 label: "Reported by",
    //                 fullWidth: true,
    //                 itemsList: useReturnAllUsers,
    //                 modelTitleParam: "full_name",
    //                 appClass: new UsersApp(),
    //                 addNewItem: addNewUser,
    //             },
    //             {
    //                 id: this.elemPrefix + "timestamp",
    //                 xs: 6,
    //                 type: "DateAndTime",
    //                 label: "Timestamp",
    //                 fullWidth: true,
    //             },
    //         ]
    //     },
    //     sectionBox: {
    //         showTitle: true,
    //         title: "Details",
    //         description: "Fill in the issue details"
    //     },
    //     requiredFields: [
    //         {
    //             id: this.elemPrefix + "item_id",
    //             message: "Item is required!"
    //         },
    //         {
    //             id: this.elemPrefix + "issue_type",
    //             message: "Issue type is required!"
    //         },
    //         {
    //             id: this.elemPrefix + "reported_by",
    //             message: "Reported by is required!"
    //         },
    //         {
    //             id: this.elemPrefix + "timestamp",
    //             message: "Timestamp by is required!"
    //         },
    //     ]
    // }

    /**
     * Requerst details
     */
    requestDetails = async(
        request_number: string
    ) => {
        return await api.get(this.appBaseEndPoint + "/details/" + request_number)
    }

    /**
     * Return requested items
     */
    requstedItemsList = async(
        request_number: string
    ) => {
        return await api.get(this.appBaseEndPoint + "/items/" + request_number)
    }

    /**
     * Approve items request
     */
    approveItemsRequest = async(
        request_number: string,
        status: string,
        note: string,
        send_email?: boolean,
        timestamp?: Date | null,
    ) => {
        return await api.post(this.appBaseEndPoint + "/approve/" + request_number, {
            status,
            note,
            send_email,
            timestamp,
        })
    }

    /**
     * Prepare items
     */
    prepareItemsForTransit = async(
        request_number: string,
        is_prepared: boolean = false,
        in_transit: boolean = false,
        note?: string,
        send_email?: boolean,
        timestamp?: Date | null,
        attachments?: IAttachment[]
    ) => {
        return await api.post(this.appBaseEndPoint + "/prepare/" + request_number, {
            is_prepared,
            in_transit,
            note,
            send_email,
            timestamp,
            attachments,
        })
    }

    /**
     * Fulfill request
     */
    fulfillRequest = async(
        request_number: string,
        note?: string,
        send_email?: boolean,
        timestamp?: Date | null,
        ignore_receiving_user?: boolean,
        attachments?: IAttachment[]
    ) => {
        return await api.post(this.appBaseEndPoint + "/fulfill/" + request_number, { note, send_email, timestamp, ignore_receiving_user, attachments })
    }

    /**
     * Return request
     */
    returnRequestedItems = async(
        request_number: string,
        note?: string,
        received?: boolean,
        requested_item_id?: number,
        only_one_item?: boolean,
        send_email?: boolean,
        timestamp?: Date | null,
        attachments?: IAttachment[]
    ) => {
        return await api.post(this.appBaseEndPoint + "/return/" + request_number, { note, received, requested_item_id, only_one_item, send_email, timestamp, attachments })
    }

    /**
     * Return request
     */
    sendBackRequestedItems = async(
        request_number: string,
        note?: string,
        send_email?: boolean,
        timestamp?: Date | null,
        ignore_receiving_user?: boolean,
        attachments?: IAttachment[],
    ) => {
        return await api.post(this.appBaseEndPoint + "/returning/" + request_number, { note, send_email, timestamp, ignore_receiving_user, attachments })
    }

    /**
     * Return request
     */
    closeItemsRequest = async(
        request_number: string,
        note?: string,
        send_email?: boolean,
        timestamp?: Date | null,
        attachments?: IAttachment[],
    ) => {
        return await api.post(this.appBaseEndPoint + "/close/" + request_number, { note, send_email, timestamp, attachments })
    }

    /**
     * Return request
     */
    rejectRequestedItem = async(
        request_number: string,
        item_id: number,
        note?: string,
    ) => {
        return await api.post(this.appBaseEndPoint + "/reject-item/" + request_number, { note, item_id })
    }

    /**
     * delete item from request request
     */
    deleteRequestedItem = async(
        request_number: string,
        item_id: number,
        note?: string,
    ) => {
        return await api.post(this.appBaseEndPoint + "/delete-item/" + request_number, { note, item_id })
    }

    /**
     * Return request log list
    */
    requestLogList = async(
        request_number: string,
        page: number, 
        search: string | null, 
        disablePagination: boolean, 
        addItems?: boolean, 
        searchParams?: ISearchParam[]
    ) => {

        let path: string = this.appBaseEndPoint + "/log/" + request_number


        path += `?page=${page ? page : 1}`

        if (search !== null){
            path += "&search=" + search
        }

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        return await api.get(path)

    }

    /**
     * Return request issues list
    */
    returnRequestIssuesList = async(
        request_number: string,
        page: number, 
        search: string | null, 
        disablePagination: boolean, 
        addItems?: boolean, 
        searchParams?: ISearchParam[]
    ) => {

        let path: string = this.appBaseEndPoint + "/issues/" + request_number


        path += `?page=${page ? page : 1}`

        if (search !== null){
            path += "&search=" + search
        }

        if (searchParams && searchParams.length > 0){
            searchParams.map((searchParam: ISearchParam) => {
                path += `&${searchParam.name}=${searchParam.value}`
            })
        }

        if (disablePagination){
            path += "&disable_pagination=1"
        }

        return await api.get(path)

    }

    /**
     * Clear requested item allocations
     */
    clearRequestedItemAllocations = async(
        request_number: string,
        item_id: number,
        warehouse_id: number,
        section_id: number,
    ) => {
        return await api.post(this.appBaseEndPoint + "/clear-item-allocations/" + request_number, { item_id, warehouse_id, section_id })
    }

    /**
     * Update requested item allocations
     */
    updateRequestedItemAllocations = async(
        request_number: string,
        item_id: number,
        warehouse_id: number,
        section_id: number,
        location_id: number,
        quantity: number,
    ) => {
        return await api.post(this.appBaseEndPoint + "/update-item-allocations/" + request_number, { item_id, warehouse_id, section_id, location_id, quantity })
    }

    /**
     * Add items to the request
     */
    addItemsToRequest = async(
        request_number: string,
        items: IRequestedItem[],
    ) => {
        return await api.post(this.appBaseEndPoint + "/add-items/" + request_number, { items })
    }

    /**
     * Export requested items
     */
    exportRequestedItems = async(
        request_number: string,
    ) => {
        return await api.post(this.appBaseEndPoint + "/export-items/" + request_number, {

        }, { responseType: "blob" })
    }

    /**
     * Export requested items
     */
    exportItemsRequestToPdf = async(
        request_number: string,
        export_type: string,
        remarks?: string,
        items_condition?: string,
        hide_request_details?: boolean,
        custom_receiving_by?: string,
        custom_received_by?: string,
        include_thumbnails?: boolean,
    ) => {
        return await api.post(this.appBaseEndPoint + "/export/" + request_number + "/pdf", {
            export_type,
            remarks,
            items_condition,
            hide_request_details,
            custom_receiving_by,
            custom_received_by,
            include_thumbnails,
        }, { responseType: "blob" })
    }

}